// Order status preview popover with hover/click interactions
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  safePolygon,
} from '@floating-ui/react';
import type { OrderStatusPreviewProps } from './types';
import OrderStatusPreviewContent from './OrderStatusPreviewContent';
import OrderStatusMobileSheet from './OrderStatusMobileSheet';
import { useOrderStatusUpdates } from '../../hooks/useOrderStatusUpdates';

// Detect touch device - only consider actual mobile/tablet devices, not laptops with trackpads
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Check user agent for actual mobile/tablet devices
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|android(?!.*mobile)/i.test(userAgent);
  // Only consider it a touch device if it's a mobile/tablet AND has touch capability
  return (isMobile || isTablet) && ('ontouchstart' in window);
};

const OrderStatusPreviewPopover: React.FC<OrderStatusPreviewProps> = ({
  orderId,
  brand,
  triggerClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [isTouch] = useState(isTouchDevice);

  // Use the lazy-loading hook
  const { data, isLoading, fetchUpdates } = useOrderStatusUpdates(orderId);

  // Track hover intent with delay
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !isTouch,
    onOpenChange: (open) => {
      if (!isPinned) {
        setIsOpen(open);
      }
    },
    middleware: [
      offset(8),
      flip({ fallbackAxisSideDirection: 'end' }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const hover = useHover(context, {
    move: false,
    enabled: !isTouch && !isPinned,
    delay: { open: 300, close: 150 },
    handleClose: safePolygon({
      blockPointerEvents: true,
    }),
  });

  const focus = useFocus(context, {
    enabled: !isTouch && !isPinned,
  });

  const dismiss = useDismiss(context, {
    enabled: !isPinned,
  });

  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  // Fetch data when popover opens
  useEffect(() => {
    if (isOpen || isMobileSheetOpen) {
      fetchUpdates();
    }
  }, [isOpen, isMobileSheetOpen, fetchUpdates]);

  // Handle click - pin on desktop, open sheet on mobile
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (isTouch) {
      setIsMobileSheetOpen(true);
    } else {
      if (isOpen) {
        setIsPinned(!isPinned);
      } else {
        setIsOpen(true);
        setIsPinned(true);
      }
    }
  }, [isTouch, isOpen, isPinned]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e as unknown as React.MouseEvent);
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      setIsOpen(false);
      setIsPinned(false);
    }
  }, [handleClick, isOpen]);

  // Close on outside click when pinned
  useEffect(() => {
    if (!isPinned) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const floating = refs.floating.current;
      const reference = refs.reference.current as Node | null;

      if (
        floating &&
        !floating.contains(target) &&
        reference &&
        !reference.contains(target)
      ) {
        setIsOpen(false);
        setIsPinned(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPinned, refs.floating, refs.reference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutRef = hoverTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  return (
    <>
      {/* Trigger button */}
      <button
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: handleClick,
          onKeyDown: handleKeyDown,
        })}
        className={`inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${triggerClassName}`}
        style={{
          color: isOpen || isPinned ? brand.colors.link : brand.colors.textLight,
          // @ts-expect-error CSS custom property
          '--tw-ring-color': brand.colors.primary,
        }}
        aria-label="View status updates"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="order-status-preview-trigger"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {/* Desktop popover */}
      {isOpen && !isTouch && (
        <FloatingPortal>
          {/* eslint-disable react-hooks/refs -- refs.setFloating is a callback ref from @floating-ui/react */}
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50"
            data-testid="order-status-preview-popover"
          >
          {/* eslint-enable react-hooks/refs */}
            {isLoading ? (
              <div
                className="rounded-lg shadow-lg p-8 flex items-center justify-center"
                style={{
                  backgroundColor: 'white',
                  border: `1px solid ${brand.colors.border}`,
                  minWidth: '200px',
                }}
              >
                <div
                  className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
                />
              </div>
            ) : data ? (
              <OrderStatusPreviewContent
                data={data}
                brand={brand}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
              />
            ) : (
              <div
                className="rounded-lg shadow-lg p-4"
                style={{
                  backgroundColor: 'white',
                  border: `1px solid ${brand.colors.border}`,
                }}
              >
                <p
                  className="text-sm"
                  style={{ color: brand.colors.textLight }}
                >
                  No status updates available
                </p>
              </div>
            )}
          </div>
        </FloatingPortal>
      )}

      {/* Mobile bottom sheet */}
      <OrderStatusMobileSheet
        isOpen={isMobileSheetOpen}
        onClose={() => setIsMobileSheetOpen(false)}
        data={data}
        isLoading={isLoading}
        brand={brand}
      />
    </>
  );
};

export default OrderStatusPreviewPopover;
